import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
    type ReactNode,
} from 'react';
import { onIdTokenChanged } from 'firebase/auth';
import { deleteObject, ref } from 'firebase/storage';
import {
    Booking,
    BookingData,
    BookingImpl,
    EventCategory,
    EventData,
    EventSlot,
    EventSlotImpl,
    SlotStatus,
} from '../types';
import { auth, storage } from '../firebase/config';
import { eventsApi } from '../api/eventsApi';
import { hasVerifiedTotp } from '../auth/authState';
import { useYearContext } from './YearContext';
import { useSyncSignal } from './SheetSyncContext';
import type { AnswerPayload } from '../lib/eventQuestions';

export type NewEventInput = Omit<
    EventSlot,
    'id' | 'booking' | 'bookings' | 'waitlist' | 'getName' | 'getDescription' | 'getStatus' | 'isRobeRotation'
> & { waitlist?: Booking[]; docId?: string };

export interface BulkEventPatch {
    name?: { en: string; si: string };
    description?: { en: string; si: string };
    date?: string;
    endDate?: string;
    startTime?: string;
    endTime?: string;
    categoryId?: string | null;
    action?: string;
    // `null` clears an existing limit — the backend maps it to
    // FieldValue.delete() in FirestoreEventAdministration.eventWrite().
    bookingLimit?: number | null;
    bookingLimitLabel?: { en: string; si: string } | null;
    bookingEmailTemplate?: string | null;
    bookingCodeRequired?: boolean;
    bookingCode?: string | null;
    specialNote?: string | null;
    status?: SlotStatus;
    waitlistEnabled?: boolean;
}

export interface AllocationInput {
    status: SlotStatus;
    booking?: Omit<Booking, 'id' | 'timestamp'> | null;
    notify?: boolean;
    specialNote?: string | null;
    bookingCodeRequired?: boolean;
    bookingCode?: string | null;
}

export type AdminEventDataScope = 'events' | 'bookings';

interface EventsContextType {
    events: EventSlot[];
    bookings: Booking[];
    canViewCancelledBookings: boolean;
    isLoading: boolean;
    addEvent: (event: NewEventInput) => Promise<EventSlot>;
    addEvents: (events: NewEventInput[]) => Promise<EventSlot[]>;
    updateEvents: (events: Array<{eventId: string; event: EventSlot}>) => Promise<void>;
    updateEvent: (event: EventSlot) => Promise<void>;
    deleteEvent: (eventId: string) => Promise<void>;
    updateEventSeries: (event: EventSlot) => Promise<void>;
    deleteEventSeries: (seriesId: string) => Promise<void>;
    bulkDeleteEvents: (eventIds: string[]) => Promise<void>;
    bulkUpdateEvents: (eventIds: string[], patch: BulkEventPatch) => Promise<void>;
    addBooking: (eventId: string, booking: Omit<Booking, 'id' | 'timestamp'>) => Promise<void>;
    cancelBooking: (eventId: string, email?: string) => Promise<void>;
    rebookBooking: (bookingId: string) => Promise<void>;
    removeWaitlistEntry: (eventId: string, email: string) => Promise<void>;
    promoteWaitlistEntry: (eventId: string, email: string) => Promise<void>;
    updateBookingDetails: (
        bookingId: string,
        patch: {
            devoteeName?: string;
            email?: string;
            phone?: string;
            address?: string;
            notes?: string;
        },
    ) => Promise<void>;
    bulkCancelBookings: (eventIds: string[]) => Promise<void>;
    addToWaitlist: (
        eventId: string,
        booking: Omit<Booking, 'id' | 'timestamp' | 'answers'> & { answers?: AnswerPayload; bookingCode?: string },
        turnstileToken: string,
    ) => Promise<void>;
    allocateEvent: (eventId: string, allocation: AllocationInput) => Promise<void>;
    preAllocateEvents?: (eventIds: string[], specialNote?: string, bookingCode?: string) => Promise<void>;
    eventCategories: EventCategory[];
    addEventCategory: (category: Omit<EventCategory, 'id'>) => Promise<void>;
    updateEventCategory: (category: EventCategory) => Promise<void>;
    deleteEventCategory: (id: string) => Promise<void>;
    setAdminDataScope?: (scope: AdminEventDataScope) => void;
}

const EVENT_AUTHORING_FIELDS = [
    'name',
    'description',
    'summary',
    'venue',
    'startTime',
    'endTime',
    'type',
    'date',
    'endDate',
    'seriesId',
    'year',
    'imageUrl',
    'categoryId',
    'bookingLimit',
    'bookingLimitLabel',
    'bookingEmailTemplate',
    'bookingCodeRequired',
    'bookingCode',
    'specialNote',
    'action',
    'eventType',
    'status',
    'collectionTime',
    'overnight',
    // QUESTIONS-01: admin-authored per-event questions. Missing from this
    // list meant EventFormModal built a `questions` payload that never left
    // the browser — eventWriteData() silently drops any field not named
    // here before the PUT ever goes out.
    'questions',
    'featuredOnHome',
    'waitlistEnabled',
] as const;

const CLEARABLE_EVENT_FIELDS = new Set(['imageUrl', 'endDate', 'bookingLimit', 'bookingLimitLabel', 'categoryId', 'bookingEmailTemplate', 'summary', 'venue', 'specialNote', 'questions', 'bookingCode']);

// Compile-time guard against a repeat of the `questions` bug: every
// authorable EventSlot field (i.e. everything except enrollment state,
// derived counts, and methods) must be listed in EVENT_AUTHORING_FIELDS, or
// eventWriteData() silently drops it before it ever reaches the Event API.
// If this block fails to compile, a field was added to/renamed on EventSlot
// without a matching update here (or vice versa) — fix the array above, not
// this type.
type NonAuthoringEventSlotField =
    | 'id' | 'booking' | 'bookings' | 'waitlist'
    | 'confirmedCount' | 'waitlistCount' | 'availability'
    | 'getName' | 'getDescription' | 'getStatus' | 'isRobeRotation'
    | 'bookingCodeHash';
type ExpectedAuthoringField = Exclude<keyof EventSlot, NonAuthoringEventSlotField>;
type ActualAuthoringField = typeof EVENT_AUTHORING_FIELDS[number];
type _NoFieldMissingFromWhitelist = [ExpectedAuthoringField] extends [ActualAuthoringField] ? true : never;
type _NoStaleWhitelistEntry = [ActualAuthoringField] extends [ExpectedAuthoringField] ? true : never;
const _authoringFieldsCoverWholeSlot: [_NoFieldMissingFromWhitelist, _NoStaleWhitelistEntry] = [true, true];
void _authoringFieldsCoverWholeSlot;

const eventWriteData = (
    event: NewEventInput | EventSlot,
    clearMissing = false,
): Record<string, unknown> => {
    const data: Record<string, unknown> = {};
    for (const field of EVENT_AUTHORING_FIELDS) {
        const value = event[field];
        if (clearMissing && value === undefined && CLEARABLE_EVENT_FIELDS.has(field)) {
            data[field] = null;
        } else if (value !== undefined) {
            data[field] = value;
        }
    }
    return data;
};

const toEvent = (data: Record<string, unknown>, enrollments: Record<string, unknown>[] = []): EventSlot => {
    const eventId = typeof data.id === 'string' ? data.id : '';
    const canonicalBookings = enrollments.filter(booking =>
        booking.eventId === eventId && booking.status === 'confirmed');
    const canonicalWaitlist = enrollments.filter(booking =>
        booking.eventId === eventId && booking.status === 'waitlisted');
    const eventData = {
        ...data,
        booking: data.booking ?? canonicalBookings[0] ?? null,
        bookings: Array.isArray(data.bookings) && data.bookings.length > 0
            ? data.bookings
            : canonicalBookings,
        waitlist: Array.isArray(data.waitlist) && data.waitlist.length > 0
            ? data.waitlist
            : canonicalWaitlist,
    } as unknown as EventData;
    return new EventSlotImpl(eventData, eventData.date || '');
};

const toBooking = (data: Record<string, unknown>): Booking =>
    new BookingImpl(data as unknown as BookingData);

export const EventsContext = createContext<EventsContextType | undefined>(undefined);

export const EventsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { selectedYear } = useYearContext();
    const syncSignal = useSyncSignal();
    const [events, setEvents] = useState<EventSlot[]>([]);
    const [bookings, setBookings] = useState<Booking[]>([]);
    const [canViewCancelledBookings, setCanViewCancelledBookings] = useState(false);
    const [eventCategories, setEventCategories] = useState<EventCategory[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [adminDataScope, setAdminDataScopeState] = useState<AdminEventDataScope>('events');
    const requestGeneration = useRef(0);

    const refresh = useCallback(async (preferAdmin = Boolean(auth.currentUser)) => {
        const generation = ++requestGeneration.current;
        setIsLoading(true);
        try {
            if (preferAdmin && await hasVerifiedTotp(auth.currentUser)) {
                try {
                    const response = adminDataScope === 'bookings'
                        ? await eventsApi.adminBookingsList(selectedYear)
                        : await eventsApi.adminList(selectedYear);
                    if (generation !== requestGeneration.current) return;
                    setEvents(response.events.map(event => toEvent(event, response.bookings ?? [])));
                    setBookings(response.bookings.map(toBooking));
                    setCanViewCancelledBookings(Boolean(response.canViewCancelledBookings));
                    setEventCategories(response.categories as unknown as EventCategory[]);
                    return;
                } catch (error) {
                    if (generation !== requestGeneration.current) return;
                    // A signed-in admin whose fresh token has not completed TOTP
                    // still gets the safe public projection while authentication
                    // finishes; no enrollment PII is exposed by the fallback.
                    console.warn('Admin Event API unavailable; using public projection', error);
                }
            }

            const response = await eventsApi.publicList(selectedYear);
            if (generation !== requestGeneration.current) return;
            setEvents(response.events.map(event => toEvent(event)));
            setBookings([]);
            setCanViewCancelledBookings(false);
            setEventCategories(response.categories as unknown as EventCategory[]);
        } finally {
            if (generation === requestGeneration.current) setIsLoading(false);
        }
    }, [adminDataScope, selectedYear]);

    useEffect(() => {
        let active = true;
        const unsubscribe = onIdTokenChanged(auth, user => {
            void refresh(Boolean(user)).catch(error => {
                if (active) console.error('Failed to fetch Events through the Event API', error);
            });
        });
        return () => {
            active = false;
            unsubscribe();
        };
    }, [refresh, syncSignal]);

    const refreshAdmin = useCallback(async () => refresh(true), [refresh]);

    const setAdminDataScope = useCallback((scope: AdminEventDataScope) => {
        setAdminDataScopeState(current => {
            if (current === scope) return current;
            if (scope === 'events') {
                // Prevent a bookings-page response from remaining visible while
                // the Events-only request is in flight after navigation.
                setBookings([]);
                setCanViewCancelledBookings(false);
                setEvents(events => events.map(event => new EventSlotImpl({
                    ...event,
                    booking: null,
                    bookings: [],
                    waitlist: [],
                } as unknown as EventData, event.date)));
            }
            return scope;
        });
    }, []);

    const addEvent = useCallback(async (event: NewEventInput): Promise<EventSlot> => {
        const response = await eventsApi.create({
            ...eventWriteData(event),
            year: event.year || selectedYear,
        });
        const created = toEvent(response.event);
        await refreshAdmin();
        return created;
    }, [refreshAdmin, selectedYear]);

    const addEvents = useCallback(async (newEvents: NewEventInput[]): Promise<EventSlot[]> => {
        const response = await eventsApi.bulkCreate(newEvents.map(event => ({
            ...eventWriteData(event),
            year: event.year || selectedYear,
            ...(event.docId ? { docId: event.docId } : {}),
        })));
        const created = response.events.map(event => toEvent(event));
        await refreshAdmin();
        return created;
    }, [refreshAdmin, selectedYear]);

    const updateEvent = useCallback(async (event: EventSlot): Promise<void> => {
        await eventsApi.update(event.id, eventWriteData(event, true));
        await refreshAdmin();
    }, [refreshAdmin]);

    const updateEvents = useCallback(async (
        updates: Array<{eventId: string; event: EventSlot}>,
    ): Promise<void> => {
        await Promise.all(updates.map(({eventId, event}) =>
            eventsApi.update(eventId, eventWriteData(event, true))
        ));
        await refreshAdmin();
    }, [refreshAdmin]);

    const cleanupEventImage = useCallback(async (eventId: string) => {
        const event = events.find(candidate => candidate.id === eventId);
        if (!event?.imageUrl) return;
        try {
            await deleteObject(ref(storage, `events/${eventId}`));
        } catch (error) {
            console.warn('[Storage] Event image cleanup failed', error);
        }
    }, [events]);

    const deleteEvent = useCallback(async (eventId: string): Promise<void> => {
        await cleanupEventImage(eventId);
        await eventsApi.remove(eventId);
        setEvents(current => current.filter(event => event.id !== eventId));
        setBookings(current => current.filter(booking => booking.eventId !== eventId));
    }, [cleanupEventImage]);

    const updateEventSeries = useCallback(async (event: EventSlot): Promise<void> => {
        if (!event.seriesId) throw new Error('seriesId is required');
        await eventsApi.updateSeries(event.seriesId, eventWriteData(event, true));
        await refreshAdmin();
    }, [refreshAdmin]);

    const deleteEventSeries = useCallback(async (seriesId: string): Promise<void> => {
        const affected = events.filter(event => event.seriesId === seriesId);
        await Promise.allSettled(affected.map(event => cleanupEventImage(event.id)));
        await eventsApi.removeSeries(seriesId);
        await refreshAdmin();
    }, [cleanupEventImage, events, refreshAdmin]);

    const bulkDeleteEvents = useCallback(async (eventIds: string[]): Promise<void> => {
        await Promise.allSettled(eventIds.map(cleanupEventImage));
        await eventsApi.bulkRemove(eventIds);
        await refreshAdmin();
    }, [cleanupEventImage, refreshAdmin]);

    const bulkUpdateEvents = useCallback(async (
        eventIds: string[],
        patch: BulkEventPatch,
    ): Promise<void> => {
        if (eventIds.length === 0) return;
        await eventsApi.bulkUpdate(eventIds, patch as Record<string, unknown>);
        await refreshAdmin();
    }, [refreshAdmin]);

    const addBooking = useCallback(async (
        eventId: string,
        booking: Omit<Booking, 'id' | 'timestamp'>,
    ): Promise<void> => {
        await eventsApi.allocate(eventId, {status: 'allocated', booking, notify: true});
        await refreshAdmin();
    }, [refreshAdmin]);

    const cancelBooking = useCallback(async (eventId: string, email?: string): Promise<void> => {
        await eventsApi.cancelEnrollment(eventId, email);
        await refreshAdmin();
    }, [refreshAdmin]);

    const rebookBooking = useCallback(async (bookingId: string): Promise<void> => {
        await eventsApi.rebookEnrollment(bookingId);
        await refreshAdmin();
    }, [refreshAdmin]);

    const removeWaitlistEntry = useCallback(async (eventId: string, email: string): Promise<void> => {
        await eventsApi.removeWaitlistEntry(eventId, email);
        await refreshAdmin();
    }, [refreshAdmin]);

    const promoteWaitlistEntry = useCallback(async (eventId: string, email: string): Promise<void> => {
        await eventsApi.promoteWaitlistEntry(eventId, email);
        await refreshAdmin();
    }, [refreshAdmin]);

    const updateBookingDetails = useCallback(async (
        bookingId: string,
        patch: {
            devoteeName?: string;
            email?: string;
            phone?: string;
            address?: string;
            notes?: string;
        },
    ): Promise<void> => {
        await eventsApi.updateBookingDetails(bookingId, patch);
        await refreshAdmin();
    }, [refreshAdmin]);

    const bulkCancelBookings = useCallback(async (eventIds: string[]): Promise<void> => {
        for (const eventId of eventIds) {
            await eventsApi.cancelEnrollment(eventId);
        }
        await refreshAdmin();
    }, [refreshAdmin]);

    const addToWaitlist = useCallback(async (
        eventId: string,
        booking: Omit<Booking, 'id' | 'timestamp' | 'answers'> & { answers?: AnswerPayload; bookingCode?: string },
        turnstileToken: string,
    ): Promise<void> => {
        await eventsApi.joinWaitlist(eventId, booking as unknown as Record<string, unknown>, turnstileToken);
        setEvents(current => current.map(event => {
            if (event.id !== eventId) return event;
            const updated = Object.assign(Object.create(Object.getPrototypeOf(event)), event);
            updated.waitlistCount = (event.waitlistCount ?? event.waitlist.length) + 1;
            return updated;
        }));
    }, []);

    const allocateEvent = useCallback(async (
        eventId: string,
        allocation: AllocationInput,
    ): Promise<void> => {
        await eventsApi.allocate(eventId, allocation as unknown as Record<string, unknown>);
        await refreshAdmin();
    }, [refreshAdmin]);

    const preAllocateEvents = useCallback(async (eventIds: string[], specialNote?: string, bookingCode?: string): Promise<void> => {
        const trimmedCode = bookingCode?.trim();
        const results = await Promise.allSettled(eventIds.map(eventId => eventsApi.allocate(eventId, {
            status: 'allocated',
            ...(specialNote !== undefined ? { specialNote: specialNote.trim() || null } : {}),
            notify: false,
            ...(trimmedCode ? { bookingCodeRequired: true, bookingCode: trimmedCode } : {}),
        })));
        await refreshAdmin();
        if (results.some(result => result.status === 'rejected')) {
            throw new Error('One or more events could not be reserved');
        }
    }, [refreshAdmin]);

    const addEventCategory = useCallback(async (
        category: Omit<EventCategory, 'id'>,
    ): Promise<void> => {
        await eventsApi.createCategory(category as Record<string, unknown>);
        await refreshAdmin();
    }, [refreshAdmin]);

    const updateEventCategory = useCallback(async (category: EventCategory): Promise<void> => {
        const { id, ...data } = category;
        await eventsApi.updateCategory(id, data);
        await refreshAdmin();
    }, [refreshAdmin]);

    const deleteEventCategory = useCallback(async (id: string): Promise<void> => {
        await eventsApi.removeCategory(id);
        await refreshAdmin();
    }, [refreshAdmin]);

    const value = useMemo<EventsContextType>(() => ({
        events,
        bookings,
        canViewCancelledBookings,
        isLoading,
        addEvent,
        addEvents,
        updateEvents,
        updateEvent,
        deleteEvent,
        updateEventSeries,
        deleteEventSeries,
        bulkDeleteEvents,
        bulkUpdateEvents,
        addBooking,
        cancelBooking,
        rebookBooking,
        removeWaitlistEntry,
        promoteWaitlistEntry,
        updateBookingDetails,
        bulkCancelBookings,
        addToWaitlist,
        allocateEvent,
        preAllocateEvents,
        eventCategories,
        addEventCategory,
        updateEventCategory,
        deleteEventCategory,
        setAdminDataScope,
    }), [
        events,
        bookings,
        canViewCancelledBookings,
        isLoading,
        addEvent,
        addEvents,
        updateEvents,
        updateEvent,
        deleteEvent,
        updateEventSeries,
        deleteEventSeries,
        bulkDeleteEvents,
        bulkUpdateEvents,
        addBooking,
        cancelBooking,
        rebookBooking,
        removeWaitlistEntry,
        promoteWaitlistEntry,
        updateBookingDetails,
        bulkCancelBookings,
        addToWaitlist,
        allocateEvent,
        preAllocateEvents,
        eventCategories,
        addEventCategory,
        updateEventCategory,
        deleteEventCategory,
        setAdminDataScope,
    ]);

    return <EventsContext.Provider value={value}>{children}</EventsContext.Provider>;
};

export const useEvents = (): EventsContextType => {
    const context = useContext(EventsContext);
    if (!context) throw new Error('useEvents must be used within an EventsProvider');
    return context;
};
